import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import SearchBar from '../components/search/SearchBar';
import UserCard from '../components/users/UserCard';
import PostCard from '../components/posts/PostCard';
import { UserData } from '../contexts/AuthContext';

interface Post {
  id: string;
  userId: string;
  userName: string;
  userPhotoURL: string;
  content: string;
  imageUrl?: string;
  videoUrl?: string;
  likes: number;
  comments: number;
  createdAt: Timestamp | Date | null;
}

interface SearchResults {
  users: UserData[];
  posts: Post[];
  hashtags: string[];
}

const Search: React.FC = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState<SearchResults>({ users: [], posts: [], hashtags: [] });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'users' | 'posts' | 'hashtags'>('all');

  useEffect(() => {
    if (!query) return;

    const performSearch = async () => {
      setLoading(true);
      try {
        // Buscar usuarios
        const usersQuery = query.startsWith('@')
          ? query.slice(1) // Si comienza con @, buscar solo ese nombre de usuario
          : query;

        const usersRef = collection(db, 'users');
        const usersSnapshot = await getDocs(usersRef);

        const users: UserData[] = [];
        usersSnapshot.forEach((doc) => {
          const userData = doc.data() as UserData;
          // Comprobar si el nombre de usuario o el nombre completo contiene la consulta
          if (
            userData.displayName.toLowerCase().includes(usersQuery.toLowerCase()) ||
            (userData.email && userData.email.toLowerCase().includes(usersQuery.toLowerCase()))
          ) {
            users.push({
              ...userData,
              uid: doc.id
            });
          }
        });

        // Buscar publicaciones
        const postsRef = collection(db, 'posts');
        const postsSnapshot = await getDocs(postsRef);

        const posts: Post[] = [];
        const hashtags = new Set<string>();

        postsSnapshot.forEach((doc) => {
          const postData = doc.data() as Omit<Post, 'id'>;
          const postContent = postData.content.toLowerCase();
          const searchLower = query.toLowerCase();

          // Buscar hashtags en el contenido
          const hashtagRegex = /#(\w+)/g;
          let match;
          while ((match = hashtagRegex.exec(postData.content)) !== null) {
            hashtags.add(match[0]); // Añadir el hashtag completo con #
          }

          // Comprobar si el contenido contiene la consulta o si es un hashtag específico
          if (
            postContent.includes(searchLower) ||
            (query.startsWith('#') && postContent.includes(query.toLowerCase()))
          ) {
            posts.push({
              ...postData,
              id: doc.id
            });
          }
        });

        // Filtrar hashtags según la consulta si es necesario
        const filteredHashtags = query.startsWith('#')
          ? Array.from(hashtags).filter(tag => tag.toLowerCase().includes(query.toLowerCase()))
          : Array.from(hashtags);

        setResults({
          users,
          posts,
          hashtags: filteredHashtags
        });
      } catch (error) {
        console.error('Error performing search:', error);
      } finally {
        setLoading(false);
      }
    };

    performSearch();
  }, [query]);

  const renderResults = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center py-10">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-domired-600"></div>
        </div>
      );
    }

    if (!query) {
      return (
        <div className="text-center py-10">
          <p className="text-gray-500 dark:text-gray-400">Ingresa un término para buscar</p>
        </div>
      );
    }

    if (
      results.users.length === 0 &&
      results.posts.length === 0 &&
      results.hashtags.length === 0
    ) {
      return (
        <div className="text-center py-10">
          <p className="text-gray-500 dark:text-gray-400">No se encontraron resultados para "{query}"</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Mostrar resultados según la pestaña activa */}
        {(activeTab === 'all' || activeTab === 'users') && results.users.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-3">Usuarios</h2>
            <div className="space-y-3">
              {results.users.map(user => (
                <UserCard key={user.uid} user={user} />
              ))}
            </div>
          </div>
        )}

        {(activeTab === 'all' || activeTab === 'posts') && results.posts.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-3">Publicaciones</h2>
            <div className="space-y-4">
              {results.posts.map(post => (
                <PostCard key={post.id} post={post} />
              ))}
            </div>
          </div>
        )}

        {(activeTab === 'all' || activeTab === 'hashtags') && results.hashtags.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-3">Hashtags</h2>
            <div className="flex flex-wrap gap-2">
              {results.hashtags.map(tag => (
                <a
                  key={tag}
                  href={`/search?q=${encodeURIComponent(tag)}`}
                  className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-domired-600 dark:text-domired-400 hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  {tag}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <SearchBar />
      </div>

      {query && (
        <div className="mb-6">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('all')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'all'
                    ? 'border-domired-500 text-domired-600 dark:text-domired-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
              >
                Todo
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'users'
                    ? 'border-domired-500 text-domired-600 dark:text-domired-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
              >
                Usuarios
              </button>
              <button
                onClick={() => setActiveTab('posts')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'posts'
                    ? 'border-domired-500 text-domired-600 dark:text-domired-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
              >
                Publicaciones
              </button>
              <button
                onClick={() => setActiveTab('hashtags')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'hashtags'
                    ? 'border-domired-500 text-domired-600 dark:text-domired-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
              >
                Hashtags
              </button>
            </nav>
          </div>
        </div>
      )}

      {renderResults()}
    </div>
  );
};

export default Search;
